<template>
  <div :class="containerClass">
    <page-header :title="title" :breadcrumb="breadcrumb" :container-class="null" class="mb-32pt" />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <b-form-group label="License" label-for="credential_type" label-class="form-label">
        <v-select
          id="credential_type"
          class="form-control v-select-custom"
          label="text"
          v-model="credential_type"
          :reduce="(item) => item.value"
          placeholder="Select License"
          :options="credentialTypeOptions"
          :clearable="false"
          :disabled="!!$route.params.id"
        >
        </v-select>
      </b-form-group>

      <b-form-group label="License Number" label-for="license_number" label-class="form-label">
        <b-form-input id="license_number" placeholder="Enter your license number" v-model="license_number" />
        <small class="form-text text-muted">
          This is the same license number as on your profile.
          <template v-if="$route.params.id">Changing it sends it back for verification.</template>
        </small>
      </b-form-group>

      <!-- Proof -->
      <page-separator title="Proof" />
      <p class="text-muted">Upload a photo of your license card or a screenshot of your state registry listing.</p>
      <b-table
        v-if="documents.length"
        :fields="docColumns"
        :items="documents"
        head-variant="light"
        class="table-nowrap mb-3"
        small
        responsive
      >
        <template #cell(kind)="data">{{ documentKindLabel(data.item.kind) }}</template>
        <template #cell(file_url)="data">
          <a :href="data.item.file_url" target="_blank" rel="noopener">{{ data.item.filename || 'View' }}</a>
        </template>
        <template #cell(actions)="data">
          <b-btn
            v-if="canRemove(data.item)"
            size="sm"
            variant="link"
            class="text-danger p-0"
            @click="removeDocument(data.index)"
          >
            <md-icon>delete</md-icon>
          </b-btn>
        </template>
      </b-table>

      <div class="row align-items-end">
        <b-form-group label="Proof Type" label-for="doc_kind" label-class="form-label" class="col-md-4">
          <v-select
            id="doc_kind"
            class="form-control v-select-custom"
            label="text"
            v-model="newDocKind"
            :reduce="(item) => item.value"
            :options="docKindOptions"
            :clearable="false"
          >
          </v-select>
        </b-form-group>
        <b-form-group label="File" label-for="doc_file" label-class="form-label" class="col-md-6">
          <b-form-file
            id="doc_file"
            placeholder="Select a file"
            accept="image/*,application/pdf"
            :browse-text="$t('generalMsgs.browse')"
            v-model="selectedUploadFile"
            @input="uploadDocument"
            :disabled="isDocUploading"
          />
        </b-form-group>
        <b-form-group class="col-md-2">
          <span v-if="isDocUploading"><i class="fas fa-circle-notch fa-spin"></i> Uploading…</span>
        </b-form-group>
      </div>

      <b-btn
        variant="primary"
        :disabled="isLoading || isDocUploading"
        style="width: 150px"
        type="submit"
        class="btn-normal"
      >
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? $t('update') : $t('add') }}</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
import { get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import PageHeader from '@/components/Ui/PageHeader.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import MdIcon from '@/components/Ui/MdIcon.vue';
import Page from '../../../components/Page.vue';
import {
  CREDENTIAL_TYPE_OPTIONS,
  DOCUMENT_KIND_OPTIONS,
  documentKindLabel,
} from '../../../common/licenseVerification';

// DRF validation errors come back as { field: [msg] } or { non_field_errors: [msg] }.
function errorMessage(err, fallback) {
  const data = get(err, 'response.data') || {};
  if (data.message && typeof data.message === 'string') return data.message;
  const first = Object.values(data).find((value) => Array.isArray(value) && value.length);
  return first ? first[0] : fallback;
}

export default {
  name: 'StudentAddLicense',
  components: { PageHeader, PageSeparator, MdIcon },
  extends: Page,

  data() {
    return {
      title: this.$route.params.id ? 'Edit License' : 'Add License',
      credential_type: 'cna',
      license_number: '',
      isLoading: false,
      credentialTypeOptions: CREDENTIAL_TYPE_OPTIONS,

      documents: [], // { id?, kind, file_url, filename, created_by? }
      deletedDocIds: [],
      newDocKind: 'license_card',
      selectedUploadFile: null,
      isDocUploading: false,
      docKindOptions: DOCUMENT_KIND_OPTIONS,
      docColumns: [
        { key: 'kind', label: 'Type' },
        { key: 'file_url', label: 'File' },
        { key: 'actions', label: '' },
      ],
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),

    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'My Licenses', to: { name: 'student-license-verifications' } },
        { text: this.title, active: true },
      ];
    },
  },

  methods: {
    ...mapActions('licenseVerification', [
      'getLicenseVerification',
      'createLicenseVerification',
      'updateLicenseVerification',
      'createLicenseDocument',
      'deleteLicenseDocument',
      'getLicenseDocUploadPresignedUrl',
    ]),
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    documentKindLabel,

    // Files an organization attached (e.g. its registry screenshot) stay put.
    canRemove(doc) {
      return !doc.id || doc.created_by === get(this.getLoggedInUser, 'id');
    },

    async uploadDocument() {
      if (!this.selectedUploadFile) return;
      this.isDocUploading = true;
      try {
        const urlResp = await this.getLicenseDocUploadPresignedUrl({
          file_name: this.selectedUploadFile.name,
          content_type: this.selectedUploadFile.type,
          upload_type: this.newDocKind,
        });
        await this.uploadToPresignedUrl({
          url: urlResp.upload_url,
          file: this.selectedUploadFile,
        });
        this.documents.push({
          kind: this.newDocKind,
          file_url: urlResp.upload_url.split('?')[0],
          filename: this.selectedUploadFile.name,
        });
        this.makeToast({ variant: 'success', msg: 'File uploaded. It will be saved with the license.' });
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Upload failed. Please try again.' });
      }
      this.selectedUploadFile = null;
      this.isDocUploading = false;
    },

    removeDocument(index) {
      const doc = this.documents[index];
      if (doc.id) this.deletedDocIds.push(doc.id);
      this.documents.splice(index, 1);
    },

    async persistDocuments(licenseId) {
      const newDocs = this.documents.filter((d) => !d.id);
      await Promise.all(
        newDocs.map((d) =>
          this.createLicenseDocument({
            license_verification: licenseId,
            kind: d.kind,
            file_url: d.file_url,
            filename: d.filename,
          })
        )
      );
      await Promise.all(this.deletedDocIds.map((id) => this.deleteLicenseDocument(id)));
      this.deletedDocIds = [];
    },

    async onSubmit() {
      if (!this.license_number.trim()) {
        this.makeToast({ variant: 'danger', msg: 'License number is required.' });
        return;
      }
      this.isLoading = true;
      try {
        const data = {
          credential_type: this.credential_type,
          license_number: this.license_number.trim(),
        };
        let licenseId = this.$route.params.id;
        if (licenseId) {
          await this.updateLicenseVerification({ id: licenseId, data });
        } else {
          const created = await this.createLicenseVerification(data);
          licenseId = get(created, 'data.id');
        }
        if (licenseId) await this.persistDocuments(licenseId);

        this.makeToast({
          variant: 'success',
          msg: this.$route.params.id ? 'License updated.' : 'License submitted for verification.',
        });
        setTimeout(() => this.$router.push({ name: 'student-license-verifications' }), 250);
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: errorMessage(err, this.$t('generalMsgs.genErrorMsg')) });
      }
      this.isLoading = false;
    },
  },

  async mounted() {
    if (this.$route.params.id) {
      this.isLoading = true;
      try {
        const resp = (await this.getLicenseVerification(this.$route.params.id)).data;
        this.credential_type = resp.credential_type;
        this.license_number = resp.license_number || '';
        this.documents = (resp.documents || []).map((d) => ({
          id: d.id,
          kind: d.kind,
          file_url: d.file_url,
          filename: d.filename,
          created_by: d.created_by,
        }));
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        this.$router.push({ name: 'student-license-verifications' });
      }
      this.isLoading = false;
    }
  },
};
</script>
